import React, { useId } from "react";
import { Input, SegmentedControl } from "../../ForBrands/ui/BrandUI";
import {
  EMPTY,
  FIRST_DATE,
  MAX_CITY,
  MAX_COMPETITION,
  MAX_OPPONENT,
  MAX_STATE,
  STATUS_DRAFT,
  STATUS_FINAL,
  gameDateText,
  homeAwayLabel,
  localToday,
} from "./gameStatsModel";

// The game above the sheet: date, opponent, home, away or neutral, where
// it was played, the competition, the final score and draft or final.
// Jayson's sheet has no score, venue or home and away, so those are
// optional. `header` is the editor's header (gameStatsModel.emptyHeader);
// `errors` are the server's field keys (date, opponent, home_away,
// location, competition, team_score, opponent_score, status). `team` gives
// the placeholders: the team's city for a home game and its league label
// for the competition.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const STATUS_NOTE = "Final games count toward player stats, CacheScore and profiles. Drafts don't count.";

const HOME_AWAY_OPTIONS = [
  { value: "home", label: "Home" },
  { value: "away", label: "Away" },
  { value: "neutral", label: "Neutral" },
];
const STATUS_OPTIONS = [
  { value: STATUS_DRAFT, label: "Draft" },
  { value: STATUS_FINAL, label: "Final" },
];

// The ids of a field's error and hint, for aria-describedby.
const described = (id, hint, error) => [error && `${id}-error`, hint && `${id}-hint`].filter(Boolean).join(" ") || undefined;

// A labelled field with its hint and error under it (the error is what a
// screen reader hears with the field).
const FormField = ({ id, label, hint, error, children, className = "", group = false }) => {
  const labelId = `${id}-label`;
  return (
    <div className={cx("flex min-w-0 flex-col gap-[6px]", className)}>
      {group ? (
        <span id={labelId} className="bm-label-m text-bm-ink">
          {label}
        </span>
      ) : (
        <label id={labelId} htmlFor={id} className="bm-label-m text-bm-ink">
          {label}
        </label>
      )}
      {group ? (
        <div role="group" aria-labelledby={labelId} aria-describedby={described(id, hint, error)}>
          {children}
        </div>
      ) : (
        children
      )}
      {hint && (
        <span id={`${id}-hint`} className="bm-body-s text-bm-muted">
          {hint}
        </span>
      )}
      {error && (
        <span id={`${id}-error`} className="bm-body-s text-bm-critical">
          {error}
        </span>
      )}
    </div>
  );
};

const ERROR_INPUT = "!border-bm-critical";

const GameHeaderForm = ({ header, onChange, team = null, errors = {}, readOnly = false, pointsSum = 0, today = localToday() }) => {
  const base = useId();
  const id = (name) => `${base}-${name}`;
  const set = (patch) => onChange?.({ ...header, ...patch }, Object.keys(patch));
  const home = header.homeAway === "home";
  const league = (team?.league_label || team?.partner_program || "").trim();
  const city = (team?.city || "").trim();
  const state = (team?.state || "").trim();

  const input = (name, key, { hint, error, ...props } = {}) => (
    <Input
      id={id(name)}
      value={header[key] ?? ""}
      onChange={(e) => set({ [key]: e.target.value })}
      disabled={readOnly}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={described(id(name), hint, error)}
      className={cx(error && ERROR_INPUT)}
      {...props}
    />
  );

  if (readOnly) {
    const where = [header.city, header.state].filter(Boolean).join(", ");
    const facts = [
      ["Date", gameDateText(header.date)],
      ["Opponent", header.opponent || EMPTY],
      ["Home or away", homeAwayLabel(header.homeAway)],
      ["Venue", where || EMPTY],
      ["Competition", header.competition || league || EMPTY],
      ["Score", header.teamScore !== "" && header.opponentScore !== "" ? `${header.teamScore}-${header.opponentScore}` : EMPTY],
      ["Status", header.status === STATUS_FINAL ? "Final" : "Draft"],
    ];
    return (
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
        {facts.map(([label, value]) => (
          <div key={label} className="flex min-w-0 flex-col gap-[2px]">
            <dt className="bm-overline">{label}</dt>
            <dd className="truncate bm-body-m text-bm-ink">{value}</dd>
          </div>
        ))}
      </dl>
    );
  }

  const competitionHint = league ? `Blank uses ${league}.` : "";
  const venueHint = home && city ? `Blank uses ${[city, state].filter(Boolean).join(", ")} for a home game.` : "";
  const scoreHint = `Players add up to ${pointsSum}`;

  return (
    <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
      <FormField id={id("date")} label="Date" error={errors.date}>
        {input("date", "date", { type: "date", min: FIRST_DATE, max: today, error: errors.date })}
      </FormField>
      <FormField id={id("opponent")} label="Opponent" error={errors.opponent}>
        {input("opponent", "opponent", { maxLength: MAX_OPPONENT, autoComplete: "off", error: errors.opponent })}
      </FormField>
      <FormField id={id("homeaway")} label="Home or away" error={errors.home_away} group>
        <SegmentedControl
          value={header.homeAway}
          capitalize={false}
          options={HOME_AWAY_OPTIONS}
          // Choosing the selected one again clears it (the field is optional).
          onChange={(value) => set({ homeAway: value === header.homeAway ? null : value })}
        />
      </FormField>
      <FormField id={id("competition")} label="Competition" hint={competitionHint} error={errors.competition}>
        {input("competition", "competition", {
          maxLength: MAX_COMPETITION,
          placeholder: league || "Competition",
          autoComplete: "off",
          hint: competitionHint,
          error: errors.competition,
        })}
      </FormField>
      <FormField id={id("city")} label="Venue city" hint={venueHint} error={errors.location}>
        {input("city", "city", {
          maxLength: MAX_CITY,
          placeholder: home && city ? city : "City",
          autoComplete: "off",
          hint: venueHint,
          error: errors.location,
        })}
      </FormField>
      <FormField id={id("state")} label="Venue state">
        {input("state", "state", { maxLength: MAX_STATE, placeholder: home && state ? state : "State", autoComplete: "off" })}
      </FormField>
      <FormField id={id("teamscore")} label="Team score" hint={scoreHint} error={errors.team_score}>
        {input("teamscore", "teamScore", { inputMode: "numeric", autoComplete: "off", hint: scoreHint, error: errors.team_score })}
      </FormField>
      <FormField id={id("opponentscore")} label="Opponent score" error={errors.opponent_score}>
        {input("opponentscore", "opponentScore", { inputMode: "numeric", autoComplete: "off", error: errors.opponent_score })}
      </FormField>
      <FormField id={id("status")} label="Status" hint={STATUS_NOTE} error={errors.status} group className="sm:col-span-2">
        <SegmentedControl value={header.status} capitalize={false} options={STATUS_OPTIONS} onChange={(value) => set({ status: value })} />
      </FormField>
    </div>
  );
};

export default GameHeaderForm;
